import type { AuthData, AuthType } from '@/models/interfaces.auth'
import { bookTutorialSetAutoShow } from '@/models/doc.tutorial'

import { saveMarketingUrl, trackLogin, trackSignup } from '@/analytics/logic'

import { auth } from '@/services/auth'
import {
  miniPlayer19040Variant,
  onboardingChecklist19055Variant,
  onboardingPlanCcCaptureV2_20676Experiment,
} from '@/services/ab/experiments'
import { resumeChecklist } from '../checklist'
import { resumePlayerSession } from '@/services/player/session'
import { startNativeProgressSync } from '@/services/player/native-progress'

/**
 * Post-authentication logic.
 *
 * Called from social login auth where we are either logging in or
 * signing up.
 * Depending on the `auth_type` we run the after-login or after-signup
 * logic.
 */
export async function afterAuth(
  authData: AuthData,
  signupType: AuthType,
): Promise<Record<string, unknown> | null> {
  if (authData.auth_type === 'login') {
    return afterLogin(authData)
  }
  return afterSignup(authData, signupType)
}

/**
 * Logic to run after user login.
 *
 * We save the user authentication data to the local storage and:
 *
 * - Save the marketing URL (if it is present) for discount detection
 * - Invoke Mixpanel.login logic
 */
export async function afterLogin(
  authData: AuthData,
): Promise<Record<string, unknown>> {
  auth.saveAuth(authData.token, authData.user)
  startNativeProgressSync()

  await saveMarketingUrl()
  await trackLogin(authData.user)

  // If the user has already started the checklist experiment,
  // we initialize the checklist with their stored progress.
  if (onboardingChecklist19055Variant() === 'B') {
    await resumeChecklist()
  }

  // Load the last doc the user viewed into the Mini Player.
  if (miniPlayer19040Variant() === 'B') {
    await resumePlayerSession()
  }

  return { name: 'discover' }
}

/*
 * Post registration logic.
 *
 * This method is unified to be used both by regular and social logins.
 *
 * Here we make the following steps:
 * - Save the auth data to the local storage.
 * - Enable book tutorial auto-show on book page visit.
 * - Start user tracking with external services.
 * - Navigate to the onboarding page.
 */
export async function afterSignup(
  authData: AuthData,
  signupType: AuthType,
): Promise<Record<string, unknown> | null> {
  auth.saveAuth(authData.token, authData.user)
  startNativeProgressSync()
  bookTutorialSetAutoShow(true)

  await trackSignup(authData.user, signupType)

  // Start exp_onboarding_plan_cc_capture_v2_20676 here, since this
  // runs for every signup surface (the standalone onboarding signup page,
  // and the signup modal on public summary/PDF pages)
  // https://github.com/shortformhq/main/issues/20192
  await onboardingPlanCcCaptureV2_20676Experiment()

  return null
}
